import React, { useState, useEffect } from 'react';
import { ChevronLeft, Tag } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import axios from 'axios';
import { API } from '../config';
import { trackPixel } from '../lib/pixel';
import { useBookingData } from '../hooks/useBookingData';
import { useBookingDeals } from '../hooks/useBookingDeals';
import BookingStepService from './booking/BookingStepService';
import BookingStepLocation from './booking/BookingStepLocation';
import BookingStepDateTime from './booking/BookingStepDateTime';
import BookingStepDetails from './booking/BookingStepDetails';
import BookingStepCheckout from './booking/BookingStepCheckout';
import BookingCompleteView from './booking/BookingCompleteView';
import { calcPersonPrice } from '../lib/pricing';

const BookingCalendar = ({ isOpen, onClose, initialService = null, initialTherapist = null, initialLocation = null }) => {
  const [step, setStep] = useState(1);
  const [services, setServices] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loyaltyInfo, setLoyaltyInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const [bookingComplete, setBookingComplete] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  const [paymentConfig, setPaymentConfig] = useState({ deposit_amount: 20, payment_enabled: false });
  const [processingPayment, setProcessingPayment] = useState(false);
  
  // Voucher state
  const [voucherCode, setVoucherCode] = useState('');
  const [voucherApplied, setVoucherApplied] = useState(null);
  const [voucherError, setVoucherError] = useState('');
  const [checkingVoucher, setCheckingVoucher] = useState(false);

  // Promo state
  const [promoCode, setPromoCode] = useState('');
  const [promoApplied, setPromoApplied] = useState(null);
  const [promoError, setPromoError] = useState('');
  const [checkingPromo, setCheckingPromo] = useState(false);
  
  const [formData, setFormData] = useState({
    service: null,
    location: '',
    therapist_preference: 'no_preference',
    therapist_preference_2: 'no_preference',
    date: '',
    time: '',
    client_name: '',
    client_email: '',
    client_phone: '',
    notes: '',
    payment_type: 'full',
    treat_recipient_name: '',
    treat_message: ''
  });

  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);

  // Custom hooks
  const { fetchPaymentConfig, fetchServices, fetchAvailableSlots, fetchLoyaltyStatus } = useBookingData({
    setPaymentConfig, setServices, setAvailableSlots, setLoyaltyInfo, formData,
  });
  const { thursdayDeal, lunchtimeDeal, firstVisitDeal } = useBookingDeals(formData);

  useEffect(() => {
    if (isOpen) {
      fetchServices();
      fetchPaymentConfig();
      if (initialTherapist) {
        setFormData(prev => ({ ...prev, therapist_preference: initialTherapist }));
      }
      if (initialLocation) {
        setFormData(prev => ({ ...prev, location: initialLocation }));
      }
      if (initialService) {
        setFormData(prev => ({ ...prev, service: initialService }));
        setStep(2);
      } else {
        setStep(1);
      }
    } else {
      setStep(1);
      setBookingComplete(false);
      setBookingResult(null);
      setPromoApplied(null);
      setPromoCode('');
      setFormData({
        service: null, location: '', therapist_preference: 'no_preference',
        therapist_preference_2: 'no_preference', date: '', time: '',
        client_name: '', client_email: '', client_phone: '', notes: '',
        payment_type: 'full', treat_recipient_name: '', treat_message: ''
      });
    }
  }, [isOpen, initialService, initialTherapist, initialLocation, fetchServices, fetchPaymentConfig]);

  useEffect(() => {
    if (formData.date && formData.location) {
      fetchAvailableSlots();
    }
  }, [formData.date, formData.location, fetchAvailableSlots]);

  useEffect(() => {
    if (formData.client_email && formData.client_email.includes('@')) {
      fetchLoyaltyStatus();
    } else {
      setLoyaltyInfo(null);
    }
  }, [formData.client_email, fetchLoyaltyStatus]);

  // Auto-fill name for returning customers
  useEffect(() => {
    if (loyaltyInfo?.customer_name && !formData.client_name) {
      setFormData(prev => ({ ...prev, client_name: loyaltyInfo.customer_name }));
    }
  }, [loyaltyInfo]);

  // Meta Pixel: user reached checkout step
  useEffect(() => {
    if (step === 5 && formData.service) {
      trackPixel('InitiateCheckout', {
        value: formData.service.price,
        currency: 'EUR',
        content_name: formData.service.name,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);


  // Voucher handlers
  const applyVoucher = async () => {
    if (!voucherCode.trim()) return;
    setCheckingVoucher(true);
    setVoucherError('');
    try {
      const response = await axios.get(`${API}/vouchers/check/${voucherCode.trim()}`);
      setVoucherApplied(response.data);
      setFormData({ ...formData, payment_type: 'voucher' });
    } catch (error) {
      setVoucherError(error.response?.data?.detail || 'Invalid voucher code');
    }
    setCheckingVoucher(false);
  };

  const removeVoucher = () => {
    setVoucherApplied(null);
    setVoucherCode('');
    setVoucherError('');
    setFormData({ ...formData, payment_type: 'full' });
  };

  // Promo handlers
  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setCheckingPromo(true);
    setPromoError('');
    try {
      const response = await axios.post(`${API}/promo/validate`, {
        code: promoCode.trim(),
        service_price: formData.service?.price || 0,
        email: formData.client_email,
        therapist: formData.therapist_preference,
        therapist_2: formData.therapist_preference_2,
        booking_date: formData.date,
        booking_time: formData.time,
      });
      // Only set promoApplied if the code is valid
      if (response.data.valid) {
        setPromoApplied(response.data);
      } else {
        setPromoError(response.data.message || 'Invalid promo code');
      }
    } catch (error) {
      setPromoError(error.response?.data?.detail || 'Invalid promo code');
    }
    setCheckingPromo(false);
  };

  const removePromo = () => {
    setPromoApplied(null);
    setPromoCode('');
    setPromoError('');
  };

  // Submit handler
  const handleSubmit = async () => {    setLoading(true);
    try {
      // Compute effective price for senior therapist(s)
      const isCouplesSubmit = formData.service.name?.toLowerCase().includes('couple');

      const calcSeniorPrice = (therapistId) => calcPersonPrice(formData.service, formData.location, therapistId);

      const pricePerson1 = calcSeniorPrice(formData.therapist_preference);
      const pricePerson2 = isCouplesSubmit ? calcSeniorPrice(formData.therapist_preference_2) : 0;
      const effectivePriceSubmit = isCouplesSubmit ? pricePerson1 + pricePerson2 : pricePerson1;

      const bookingData = {
        client_name: formData.client_name,
        client_email: formData.client_email,
        client_phone: formData.client_phone,
        service_name: formData.service.name,
        service_duration: formData.service.duration,
        service_price: effectivePriceSubmit,
        location: formData.location,
        therapist_preference: formData.therapist_preference,
        therapist_preference_2: isCouplesSubmit ? formData.therapist_preference_2 : null,
        booking_date: formData.date,
        booking_time: formData.time,
        notes: formData.notes || null,
        payment_type: voucherApplied ? 'voucher' : formData.payment_type,
        promo_code: promoApplied?.code || (lunchtimeDeal ? 'LUNCH10' : (thursdayDeal ? 'THURSDAY5' : null)),
        treat_recipient_name: promoApplied?.code === 'TREATHER' ? (formData.treat_recipient_name || '').trim() : null,
        treat_message: promoApplied?.code === 'TREATHER' ? ((formData.treat_message || '').trim() || null) : null
      };

      const response = await axios.post(`${API}/bookings`, bookingData);
      const booking = response.data;

      trackPixel('Schedule', {
        value: effectivePriceSubmit,
        currency: 'EUR',
        content_name: formData.service.name,
        content_category: 'massage_booking',
      });

      // If voucher is applied and covers full amount
      if (voucherApplied) {
        const discountAmount = loyaltyInfo?.current_discount_percent 
          ? (effectivePriceSubmit * loyaltyInfo.current_discount_percent / 100) 
          : 0;
        const finalPrice = effectivePriceSubmit - discountAmount;

        if (voucherApplied.remaining_balance >= finalPrice) {
          try {
            await axios.post(`${API}/vouchers/redeem`, null, {
              params: { code: voucherApplied.code, amount: finalPrice }
            });
            setBookingResult(booking);
            setBookingComplete(true);
            setLoading(false);
            return;
          } catch (err) {
            console.error('Voucher redeem failed:', err);
          }
        } else {
          const remainder = finalPrice - voucherApplied.remaining_balance;
          try {
            await axios.post(`${API}/vouchers/redeem`, null, {
              params: { code: voucherApplied.code, amount: voucherApplied.remaining_balance }
            });
          } catch (err) {
            console.error('Partial voucher redeem failed:', err);
          }

          if (paymentConfig.payment_enabled) {
            setProcessingPayment(true);
            try {
              const paymentRes = await axios.post(
                `${API}/payment/create-checkout`,
                {
                  booking_id: booking.id,
                  payment_type: 'voucher',
                  amount: remainder,
                  return_url: window.location.origin + '/booking-confirmed',
                }
              );
              window.location.href = paymentRes.data.checkout_url;
              return;
            } catch (err) {
              console.error('Payment checkout failed:', err);
            }
            setProcessingPayment(false);
          }
        }

        setBookingResult(booking);
        setBookingComplete(true);
        setLoading(false);
        return;
      }

      // Online payment
      let payCfg = paymentConfig;
      if (!payCfg?.payment_enabled) {
        try {
          const cfgRes = await axios.get(`${API}/payment/config`);
          payCfg = cfgRes.data;
        } catch (cfgErr) {
          console.error('Payment config refetch failed:', cfgErr);
        }
      }
      if (payCfg?.payment_enabled) {
        setProcessingPayment(true);
        const checkoutPayload = {
          booking_id: booking.id,
          payment_type: formData.payment_type || 'full',
          return_url: window.location.origin + '/booking-confirmed',
        };
        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            const paymentRes = await axios.post(`${API}/payment/create-checkout`, checkoutPayload);
            window.location.href = paymentRes.data.checkout_url;
            return;
          } catch (err) {
            console.error(`Payment checkout failed (attempt ${attempt + 1}):`, err);
          }
        }
        setProcessingPayment(false);
      }

      setBookingResult(booking);
      setBookingComplete(true);
    } catch {
      alert('Error creating booking. Please try again.');
    }
    setLoading(false);
  };

  // Booking complete view
  if (bookingComplete && bookingResult) {
    return <BookingCompleteView isOpen={isOpen} onClose={onClose} bookingResult={bookingResult} />;
  }

  // Step routing
  const renderStep = () => {
    switch(step) {
      case 1:
        return <BookingStepService services={services} formData={formData} setFormData={setFormData} setStep={setStep} />;
      case 2:
        return <BookingStepLocation formData={formData} setFormData={setFormData} setStep={setStep} />;
      case 3:
        return (
          <BookingStepDateTime
            formData={formData} setFormData={setFormData} setStep={setStep}
            availableSlots={availableSlots} currentMonth={currentMonth} setCurrentMonth={setCurrentMonth}
            selectedDate={selectedDate} setSelectedDate={setSelectedDate}
          />
        );
      case 4:
        return <BookingStepDetails formData={formData} setFormData={setFormData} setStep={setStep} loyaltyInfo={loyaltyInfo} />;
      case 5:
        return (
          <BookingStepCheckout
            formData={formData} setFormData={setFormData}
            loyaltyInfo={loyaltyInfo} paymentConfig={paymentConfig}
            promoApplied={promoApplied} promoCode={promoCode} setPromoCode={setPromoCode}
            promoError={promoError} checkingPromo={checkingPromo} applyPromo={applyPromo} removePromo={removePromo}
            thursdayDeal={thursdayDeal} lunchtimeDeal={lunchtimeDeal} firstVisitDeal={firstVisitDeal}
            voucherApplied={voucherApplied} voucherCode={voucherCode} setVoucherCode={setVoucherCode}
            voucherError={voucherError} checkingVoucher={checkingVoucher} applyVoucher={applyVoucher} removeVoucher={removeVoucher}
            handleSubmit={handleSubmit} loading={loading} processingPayment={processingPayment}
          />
        );
      default:
        return null;
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[92vw] max-w-lg max-h-[90vh] overflow-y-auto mx-auto p-3 sm:p-6 rounded-2xl bg-white border-stone-200" data-testid="booking-modal">
        <DialogHeader className="space-y-1">
          <DialogTitle className="font-display text-base sm:text-xl text-luxury-green">Book Your Experience</DialogTitle>
          <DialogDescription className="text-xs sm:text-sm text-stone-400 uppercase tracking-wider">
            Step {step} of 5
          </DialogDescription>
        </DialogHeader>

        {/* Progress Bar */}
        <div className="flex gap-1 mb-3 sm:mb-4">
          {[1, 2, 3, 4, 5].map((s) => (
            <div 
              key={s} 
              className={`h-1 flex-1 rounded-full transition-all ${s <= step ? 'bg-luxury-green' : 'bg-stone-200'}`} 
            />
          ))}
        </div>

        {/* Deposit Promo Banner */}
        {step < 5 && (
          <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 mb-3" data-testid="deposit-promo-banner">
            <Tag size={14} className="text-emerald-600 flex-shrink-0" />
            <p className="text-xs text-emerald-700">
              <span className="font-semibold">Save 5%</span> — Pay a deposit at checkout and get 5% off your treatment!
            </p>
          </div>
        )}

        {/* Step Content */}
        <div className="min-h-0 overflow-y-auto">
          {renderStep()}
        </div>

        {/* Back Button */}
        {step > 1 && !bookingComplete && (
          <Button 
            variant="ghost" 
            onClick={() => setStep(step - 1)}
            className="mt-3 sm:mt-4 text-sm"
            data-testid="booking-back-btn"
          >
            <ChevronLeft size={16} className="mr-1" />
            Back
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default BookingCalendar;
